import React, { useEffect, useState } from 'react';
import { transactionAPI } from '../../services/api';
import Sidebar from '../../components/Common/Sidebar';
import {
  ArrowDownLeft, ArrowUpRight, TrendingUp, Banknote,
  Filter, Download, Search, ChevronLeft, ChevronRight,
  CheckCircle2, XCircle, AlertCircle, Loader2,
} from 'lucide-react';
import { toast } from 'react-toastify';

/* ── design tokens ── */
const C = {
  bg:          '#f4f4f9',
  surface:     '#FFFFFF',
  border:      '#e8e8f1',
  borderSoft:  '#f0f0f6',
  ink:         '#14142b',
  body:        '#4a4a6a',
  muted:       '#6e6e86',
  primary:     '#7c3aed',
  primarySoft: '#f1ebff',
  primaryTint: '#ddd6fe',
  success:     '#10B981',
  successSoft: '#D1FAE5',
  warning:     '#F59E0B',
  warnSoft:    '#FEF3C7',
  danger:      '#EF4444',
  dangerSoft:  '#FEE2E2',
  radius:      14,
  radiusSm:    8,
};

const fmtINR = (n) =>
  '₹ ' + Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true,
  });
};

/* ── summary card ── */
const SummaryCard = ({ icon: Icon, iconColor, iconBg, label, value }) => (
  <div style={{
    background: C.surface, border: `1px solid ${C.border}`,
    borderRadius: C.radius, padding: '18px 20px',
    display: 'flex', alignItems: 'center', gap: 16,
    boxShadow: '0 1px 3px rgba(20,20,43,.04)',
    transition: 'box-shadow .15s',
  }}
  onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 16px rgba(124,58,237,.08)'}
  onMouseLeave={e => e.currentTarget.style.boxShadow = '0 1px 3px rgba(20,20,43,.04)'}
  >
    <div style={{
      width: 44, height: 44, borderRadius: 11,
      background: iconBg, color: iconColor,
      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
    }}>
      <Icon size={20} strokeWidth={1.8} />
    </div>
    <div>
      <p style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '.06em', margin: 0 }}>
        {label}
      </p>
      <p style={{ fontSize: 20, fontWeight: 800, color: C.ink, margin: '4px 0 0', letterSpacing: '-0.01em' }}>
        {value ?? '——'}
      </p>
    </div>
  </div>
);

/* ── status badge ── */
const StatusBadge = ({ status }) => {
  const map = {
    success:   { bg: C.successSoft, color: '#065F46', Icon: CheckCircle2 },
    pending:   { bg: C.warnSoft,    color: '#92400E', Icon: AlertCircle  },
    failed:    { bg: C.dangerSoft,  color: '#991B1B', Icon: XCircle      },
  };
  const s = map[status?.toLowerCase()] || map.pending;
  const { Icon } = s;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      fontSize: 11, fontWeight: 700, padding: '3px 10px',
      borderRadius: 20, background: s.bg, color: s.color,
    }}>
      <Icon size={10} strokeWidth={2.5} />
      {(status || 'pending').charAt(0).toUpperCase() + (status || 'pending').slice(1)}
    </span>
  );
};

/* ── type icon ── */
const TypeIcon = ({ type }) => {
  const map = {
    deposit:    { Icon: ArrowDownLeft, color: C.success, bg: C.successSoft },
    withdrawal: { Icon: ArrowUpRight,  color: C.danger,  bg: C.dangerSoft  },
    commission: { Icon: TrendingUp,    color: C.primary, bg: C.primarySoft },
    refund:     { Icon: Banknote,      color: C.warning, bg: C.warnSoft    },
  };
  const m = map[type?.toLowerCase()] || map.refund;
  return (
    <div style={{
      width: 28, height: 28, borderRadius: 8,
      background: m.bg, color: m.color,
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
    }}>
      <m.Icon size={13} strokeWidth={2} />
    </div>
  );
};

/* ── filter input style ── */
const filterInput = {
  width: '100%', padding: '8px 12px',
  fontSize: 12, color: C.ink,
  background: C.bg, border: `1px solid ${C.border}`,
  borderRadius: C.radiusSm, outline: 'none',
  boxSizing: 'border-box',
};

const TH_COLS = ['Transaction ID', 'Type', 'Amount', 'Gateway', 'Status', 'Description', 'Date & Time'];

const UserTransactions = () => {
  const [loading,      setLoading]      = useState(true);
  const [transactions, setTransactions] = useState([]);
  const [pagination,   setPagination]   = useState(null);
  const [filters, setFilters] = useState({
    type: 'all', status: 'all', search: '', date_from: '', date_to: '', page: 1, per_page: 15,
  });

  useEffect(() => { fetchTransactions(); /* eslint-disable-next-line */ }, [filters]);

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const params = { ...filters };
      if (params.type   === 'all') delete params.type;
      if (params.status === 'all') delete params.status;
      const res = await transactionAPI.getTransactions(params);
      setTransactions(res.data.data.transactions);
      setPagination(res.data.data.pagination);
    } catch {
      toast.error('Failed to load transactions');
    } finally {
      setLoading(false);
    }
  };


  const setFilter = (key, value) =>
    setFilters(p => ({ ...p, [key]: value, page: 1 }));

  const handleExport = async () => {
    try {
      const res = await transactionAPI.exportTransactions();
      const url  = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href  = url;
      link.setAttribute('download', `transactions_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Exported successfully');
    } catch {
      toast.error('Export failed');
    }
  };

  const pages = pagination ? Math.min(pagination.last_page, 7) : 0;
  const currentPage = pagination?.current_page || 1;

  return (
    <div className="dashboard-layout">
      <Sidebar role="user" />

      <div className="dashboard-content" style={{ background: C.bg, minHeight: '100vh', padding: '28px 28px 40px' }}>

        {/* ── page header ── */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 26, flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h2 style={{ fontSize: 22, fontWeight: 800, color: C.ink, margin: 0 }}>Transaction History</h2>
            <p  style={{ fontSize: 13, color: C.muted, marginTop: 4 }}>View and manage all your transactions</p>
          </div>
          <button
            onClick={handleExport}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 7,
              padding: '9px 18px', borderRadius: C.radiusSm,
              background: C.surface, color: C.primary,
              border: `1.5px solid ${C.primaryTint}`,
              fontWeight: 700, fontSize: 13, cursor: 'pointer',
            }}
          >
            <Download size={14} strokeWidth={2} /> Export CSV
          </button>
        </div>


        {/* ── filters ── */}
        <div style={{ background: C.surface, border: `1.5px solid ${C.border}`, borderRadius: C.radiusSm, padding: '10px 16px', marginBottom: 18, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {/* Quick status pills */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '.06em', marginRight: 4 }}>Status</span>
            {[
              { label: 'All',     val: 'all',     color: C.primary },
              { label: 'Success', val: 'success', color: C.success },
              { label: 'Pending', val: 'pending', color: C.warning },
              { label: 'Failed',  val: 'failed',  color: C.danger  },
            ].map(t => (
              <button key={t.val} onClick={() => setFilter('status', t.val)}
                style={{ height: 32, padding: '0 13px', borderRadius: 999, border: `1.5px solid ${filters.status === t.val ? t.color : C.border}`, background: filters.status === t.val ? t.color : 'transparent', color: filters.status === t.val ? '#fff' : C.body, fontWeight: 600, fontSize: 12, cursor: 'pointer', transition: 'all .15s', fontFamily: 'inherit' }}>
                {t.label}
              </button>
            ))}
            <div style={{ width: 1, height: 22, background: C.border, margin: '0 4px' }} />
            <span style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '.06em', marginRight: 4 }}>Type</span>
            {[
              { label: 'All',        val: 'all'        },
              { label: 'Deposit',    val: 'deposit'    },
              { label: 'Withdrawal', val: 'withdrawal' },
              { label: 'Commission', val: 'commission' },
            ].map(t => (
              <button key={t.val} onClick={() => setFilter('type', t.val)}
                style={{ height: 32, padding: '0 13px', borderRadius: 999, border: `1.5px solid ${filters.type === t.val ? C.primary : C.border}`, background: filters.type === t.val ? C.primarySoft : 'transparent', color: filters.type === t.val ? C.primary : C.body, fontWeight: 600, fontSize: 12, cursor: 'pointer', transition: 'all .15s', fontFamily: 'inherit' }}>
                {t.label}
              </button>
            ))}
          </div>
          {/* Search + dates row */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <div style={{ position: 'relative', flex: '1 1 200px' }}>
              <Search size={13} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: C.muted, pointerEvents: 'none' }} />
              <input type="text" placeholder="Search Transaction ID…" value={filters.search} onChange={e => setFilter('search', e.target.value)} style={{ ...filterInput, paddingLeft: 30, height: 36 }} />
            </div>
            <input type="date" value={filters.date_from} onChange={e => setFilter('date_from', e.target.value)} style={{ ...filterInput, width: 150, height: 36 }} />
            <input type="date" value={filters.date_to} onChange={e => setFilter('date_to', e.target.value)} style={{ ...filterInput, width: 150, height: 36 }} />
          </div>
        </div>

        {/* ── transactions table ── */}
        <div style={{
          background: C.surface, border: `1px solid ${C.border}`,
          borderRadius: C.radius, padding: '20px 24px',
          boxShadow: '0 1px 3px rgba(20,20,43,.04)',
        }}>
          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
              <Loader2 size={30} color={C.primary} style={{ animation: 'spin 1s linear infinite' }} />
            </div>
          ) : (
            <>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ borderBottom: `2px solid ${C.borderSoft}` }}>
                      {TH_COLS.map(h => (
                        <th key={h} style={{
                          padding: '9px 14px', textAlign: 'left',
                          fontSize: 11, fontWeight: 700, color: C.muted,
                          textTransform: 'uppercase', letterSpacing: '.06em', whiteSpace: 'nowrap',
                        }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.length > 0 ? transactions.map(txn => (
                      <tr key={txn.id}
                        style={{ borderBottom: `1px solid ${C.borderSoft}`, transition: 'background .1s' }}
                        onMouseEnter={e => e.currentTarget.style.background = C.bg}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                      >
                        <td style={{ padding: '12px 14px', fontFamily: 'monospace', fontSize: 12, color: C.body, whiteSpace: 'nowrap' }}>
                          {txn.transaction_id}
                        </td>
                        <td style={{ padding: '12px 14px' }}>
                          {txn.status?.toLowerCase() === 'success' ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <TypeIcon type="deposit" />
                              <span style={{ color: C.body }}>Deposit</span>
                            </div>
                          ) : (
                            <span style={{ color: C.muted }}>—</span>
                          )}
                        </td>
                        <td style={{ padding: '12px 14px', fontWeight: 800, color: C.ink, whiteSpace: 'nowrap' }}>
                          {fmtINR(txn.amount)}
                        </td>
                        <td style={{ padding: '12px 14px' }}>
                          <span style={{
                            fontSize: 11, fontWeight: 700, padding: '3px 8px',
                            borderRadius: 6, background: C.bg, color: C.body,
                            border: `1px solid ${C.border}`,
                          }}>
                            A1XPAY
                          </span>
                        </td>
                        <td style={{ padding: '12px 14px' }}>
                          <StatusBadge status={txn.status} />
                        </td>
                        <td style={{ padding: '12px 14px', color: C.muted, fontSize: 12, maxWidth: 200 }}>
                          <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {txn.description || '—'}
                          </span>
                        </td>
                        <td style={{ padding: '12px 14px', color: C.muted, fontSize: 12, whiteSpace: 'nowrap' }}>
                          {fmtDate(txn.created_at)}
                        </td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan={TH_COLS.length} style={{
                          padding: '60px 0', textAlign: 'center',
                          color: C.muted, fontSize: 14,
                        }}>
                          No transactions found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* ── pagination ── */}
              {pagination && pagination.last_page > 1 && (
                <div style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  marginTop: 20, paddingTop: 16, borderTop: `1px solid ${C.borderSoft}`,
                  flexWrap: 'wrap', gap: 10,
                }}>
                  <span style={{ fontSize: 12, color: C.muted }}>
                    Showing {pagination.from}–{pagination.to} of {pagination.total} transactions
                  </span>

                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    {/* prev */}
                    <button
                      onClick={() => setFilters(p => ({ ...p, page: p.page - 1 }))}
                      disabled={currentPage === 1}
                      style={{
                        width: 32, height: 32, borderRadius: 8, border: `1px solid ${C.border}`,
                        background: C.surface, cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                        color: currentPage === 1 ? C.muted : C.body,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}
                    >
                      <ChevronLeft size={14} />
                    </button>

                    {/* page numbers */}
                    {Array.from({ length: pagination.last_page }, (_, i) => i + 1)
                      .filter(p => {
                        const last = pagination.last_page;
                        return p === 1 || p === last || Math.abs(p - currentPage) <= 2;
                      })
                      .reduce((acc, p, idx, arr) => {
                        if (idx > 0 && p - arr[idx - 1] > 1) acc.push('…');
                        acc.push(p);
                        return acc;
                      }, [])
                      .map((p, i) =>
                        p === '…' ? (
                          <span key={`ellipsis-${i}`} style={{ padding: '0 4px', color: C.muted, fontSize: 13 }}>…</span>
                        ) : (
                          <button key={p}
                            onClick={() => setFilters(prev => ({ ...prev, page: p }))}
                            style={{
                              width: 32, height: 32, borderRadius: 8,
                              border: `1px solid ${p === currentPage ? C.primary : C.border}`,
                              background: p === currentPage ? C.primary : C.surface,
                              color: p === currentPage ? '#fff' : C.body,
                              fontWeight: p === currentPage ? 700 : 400,
                              fontSize: 13, cursor: 'pointer',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}
                          >
                            {p}
                          </button>
                        )
                      )
                    }

                    {/* next */}
                    <button
                      onClick={() => setFilters(p => ({ ...p, page: p.page + 1 }))}
                      disabled={currentPage === pagination.last_page}
                      style={{
                        width: 32, height: 32, borderRadius: 8, border: `1px solid ${C.border}`,
                        background: C.surface,
                        cursor: currentPage === pagination.last_page ? 'not-allowed' : 'pointer',
                        color: currentPage === pagination.last_page ? C.muted : C.body,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}
                    >
                      <ChevronRight size={14} />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <style>{`
        @keyframes spin{to{transform:rotate(360deg)}}
        .txn-filter-grid {
          display: grid;
          grid-template-columns: repeat(4,1fr) 2fr;
          gap: 10px 14px;
        }
        @media (max-width: 700px) {
          .txn-filter-grid { grid-template-columns: 1fr 1fr; }
        }
        @media (max-width: 400px) {
          .txn-filter-grid { grid-template-columns: 1fr; }
        }
      `}</style>
    </div>
  );
};

export default UserTransactions;
